import React from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { Row, Col } from 'react-bootstrap';

import EntityStateUpdater from 'component/EntityStateUpdater';

import { formStatusDispatchHelper, formReset } from 'actions/FormStatusActions';
import { blockForm, releaseForm } from 'actions/ActiveFormsActions';
import { addNotification } from 'actions/NotificationsActions';

import Breadcrumb from 'components/layout/Breadcrumb';
import Tabs from 'components/layout/Tabs';
import Loader from 'components/layout/Loader';

import PageCanvas from 'components/layout/cms/PageCanvas';
import PageData from 'components/layout/cms/PageData';
import PageSeo from 'components/layout/cms/PageSeo';
import PageHistory from 'components/layout/cms/PageHistory';

import Entity from 'components/Entity';
import Data from 'components/Data';

import { entityMetaMapping } from 'js/constants';

import ActiveFormsToolbar from 'components/forms/ActiveFormsToolbar';
import FormErrors from 'components/forms/FormErrors';

import DocMeta from 'components/DocMeta';
import { generateMetaTags } from 'helpers/metaTags';
import may from "js/security";

class CmsDetails extends EntityStateUpdater {
  constructor(props) {
    super(props);

    const { pageData, meta } = props;

    this.state = {
      entityLoaded: false,
      entityChanged: false,
      activeFormsInitiated: false,
      owner: pageData && pageData.owner && { ...pageData.owner },
    };

    this.entity = new Entity();

    // if we get page data from the outside, we can pass it on to our data entity
    if (pageData) {
      this.entity.setData(pageData);
    }

    if (meta[entityMetaMapping.page] !== undefined && !meta[entityMetaMapping.page].pending) {
      this.entity.setMeta(meta, entityMetaMapping.page);
    }

    if (this.entity.getDataLoaded() && this.entity.getMetaLoaded()) {
      this.state = {
        ...this.state,
        entityLoaded: true,
        entity: this.entity,
      };
    }

    this.dataRef = React.createRef();
  }

  componentDidUpdate = (prevProps, prevState) => {
    const {
      intl: { messages },
      cmsId,
      match,
      meta,
      formStatus,
      activeForms,
      data,
      doBlockForm,
      doNotify,
    } = this.props;
    const { entity, entityLoaded, activeFormsInitiated } = this.state;

    if (!prevProps.activeForms.allowEdit && activeForms.allowEdit) {
      if (this.dataRef && this.dataRef.current) {
        this.dataRef.current.fetch();
      }

      this.setState({ entityLoaded: false });
    }

    // page data was saved and there was no error
    if (prevProps.formStatus.pending && !formStatus.pending && !formStatus.hasError) {
      this.setState({ entityChanged: false });
      doNotify(1, messages.page_saved);
    }

    if (prevProps.data.pending && !data.pending) {
      this.entity.setData(data.data);
      if (this.entity.getMetaLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }

      // we received the page data
      // now we have to see if we can block the page edit to ourselves
      if (!activeFormsInitiated && !may('ROLE_ROOT_EMPLOYEE', this.props.user.roles)) {
        this.setState({ activeFormsInitiated: true }, () => doBlockForm(cmsId || match.params.cmsId));
      }
    }

    if (
      meta[entityMetaMapping.page] !== undefined &&
      !meta[entityMetaMapping.page].pending &&
      !this.entity.getMetaLoaded()
    ) {
      this.entity.setMeta(meta, entityMetaMapping.page);
      if (this.entity.getDataLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    }

    if (entityLoaded && !prevState.entityLoaded && entity.data && entity.data.owner) {
      this.setState({ owner: { ...entity.data.owner } });
    }
  };

  componentWillUnmount = () => {
    const { cmsId, match, doReleaseForm } = this.props;
    // release the block so that other users may work on the page
    doReleaseForm(cmsId || match.params.cmsId);
  };

  formSubmit = e => {
    e.preventDefault();

    const { activeForms } = this.props;
    const { entity: beforeValidation } = this.state;

    if (activeForms.allowEdit) {
      this.changeEntity(beforeValidation.validate(), () => {
        const { cmsId, match, onSubmit } = this.props;
        const { entity: afterValidation, owner } = this.state;

        if (afterValidation.isValid) {
          onSubmit(cmsId || match.params.cmsId, { ...afterValidation.processedData, owner });
        }
      });
    }
  };

  getTabHeaderList = () => {
    const {
      intl: { messages },
    } = this.props;
    const result = [];

    result.push(messages.page_design);
    result.push(messages.page);
    result.push(messages.seo);
    result.push(messages.timeline);

    return result;
  };

  getTabContentList = () => {
    const { formStatus, activeForms } = this.props;
    const { entity, entityLoaded, owner } = this.state;
    const result = [];

    const disabled = formStatus.pending || !activeForms.allowEdit;
    const sharedProps = { entityLoaded, entity, changeEntity: this.changeEntity, disabled };

    result.push(entityLoaded ? <PageCanvas {...sharedProps} /> : <Loader />);
    result.push(
      <PageData
        {...sharedProps}
        owner={owner}
        updateOwner={this.updateOwner}
        updateWhiteLabelProject={this.updateWhiteLabelProject}
      />
    );
    result.push(<PageSeo {...sharedProps} />);
    result.push(<PageHistory {...sharedProps} />);

    return result;
  };

  getTabKeepMounted = () => {
    const keepMounted = [0, 1];
    return keepMounted;
  };

  getBreadcrumb = () => {
    if (this.props.breadcrumb) {
      return this.props.breadcrumb;
    }

    const {
      intl: { messages },
      crumbs: propsCrumbs,
      appIntl,
    } = this.props;
    const { entityLoaded, entity } = this.state;

    let pageHeader;
    if (entityLoaded) {
      pageHeader = entity.getName();
    }

    if (propsCrumbs) {
      const crumbs = propsCrumbs.slice();
      crumbs.push({ link: null, name: pageHeader });

      return <Breadcrumb crumbs={crumbs} className="p-t-0" />;
    }

    return (
      <Breadcrumb
        crumbs={[
          { link: appIntl.urlPrefix + 'account', name: 'Dashboard' },
          { link: appIntl.urlPrefix + 'account/cms', name: messages.cms_pages },
          { link: null, name: entityLoaded ? entity.getName() : null },
        ]}
        className="p-t-0"
      />
    );
  };

  updateOwner = (fieldName, owner) => {
    const { owner: currentOwner } = this.state;
    const entityChanged = owner && owner.username !== (currentOwner && currentOwner.username);

    this.setState({ owner, entityChanged });
  };

  updateWhiteLabelProject = (fieldName, whiteLabelProject) => {
    this.updateEntity(fieldName, whiteLabelProject.id);
  };

  render() {
    const {
      intl: { messages },
      cmsId,
      match,
      data,
      formStatus,
    } = this.props;
    const { entity, entityLoaded, entityChanged } = this.state;

    let metaTitle = messages.page;
    if (entityLoaded) {
      metaTitle += ' - ' + entity.getName();
    }

    return (
      <DocMeta tags={generateMetaTags(messages.meta.page_details, metaTitle)}>
        <div className="padding-25 sm-padding-10">
          <Data ref={this.dataRef} dataName="page" url={`/api/pages/${cmsId || match.params.cmsId}.json`} data={data} />
          <FormErrors formStatus={formStatus} />

          <form method="put" onSubmit={this.formSubmit} key={entityLoaded ? entity.getId() : 1}>
            {this.getBreadcrumb()}

            <Row>
              <Col xs={12} className="p-b-10">
                <ActiveFormsToolbar
                  formStatus={formStatus}
                  entityChanged={entityChanged}
                  formType="page"
                  formId={cmsId || match.params.cmsId}
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12}>
                <Tabs
                  tabs={this.getTabHeaderList()}
                  tabContent={this.getTabContentList()}
                  keepMounted={this.getTabKeepMounted()}
                />
              </Col>
            </Row>
          </form>
        </div>
      </DocMeta>
    );
  }
}

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
    data: state.data.page,
    formStatus: state.formStatus.pageEdit,
    meta: state.entityMeta,
    activeForms: state.activeForms,
    user: state.login.user,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    doNotify: (code, text) => {
      dispatch(addNotification(code, text));
    },
    onSubmit: (cmsId, args) => {
      formStatusDispatchHelper('pageEdit', `/api/pages/${cmsId}.json`, args, 'put', dispatch);
    },
    doBlockForm: id => {
      dispatch(blockForm('page', id));
    },
    doReleaseForm: id => {
      dispatch(releaseForm('page', id));
    },
  };
};

export default injectIntl(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(CmsDetails)
);
